import * as moment from 'moment';
import * as React from 'react';
import styled from 'styled-components';
import { CellObject, utils, writeFileXLSX } from 'xlsx';
import { Campaign } from '../../../atoms/campaignAtom';
import { getDeletedProducts, Product } from '../../../atoms/product';
import Popup from '../../../components_v2/popup/Popup';
import { ButtonStyle } from '../../../components_v2/popup/PopupCreation';
import { PopupMode } from '../../../components_v2/popup/model/Model';
import { DefaultButton } from '../../../styles/global/css/GlobalButton';
import { translateToString } from '../../../styles/global/translate';
import { Owner } from '../../orders/model/Model';
import { FlexDiv } from '../../products/style';
import { calcEvolution, goalPathHash, reportingDateToString } from '../interpretor/ReportInterpretor';
import { Cell, ComplexCell, Header, Report, Row, Tooltip, Value } from '../interpretor/bareReportingDecoder';
import { EvolutionState } from '../interpretor/hoverContext';
import { GoalHashMap } from './action';


const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex-wrap: nowrap;
	justify-content: space-between;
    height: 100%;
	padding: 10px;
`;

const Body = styled.div`
    display: flex;
    flex-direction: column;
	gap: 10px;
`;

const Title = styled.h2`
	margin: 0;
	padding: 0;
	font-size: 16px;
`;

const Button = styled(DefaultButton)`
    width: 200px;
    height: 40px;
`;

const Text = styled.div`
	font-size: 14px;
`;

function safeStringTruncate(str: string, maxLength: number, invalidCharsRegex = /[/\\?*:[\]]/g): string {
	const cleaned = str.replace(invalidCharsRegex, '-');
	// Use Array.from to properly handle multi-byte characters like emojis
	const chars = Array.from(cleaned);
	if (chars.length <= maxLength) {
		return cleaned;
	}
	return chars.slice(0, maxLength).join('');
}

function getGoalPathHash(primaryCells: Cell[], headers: Header[], date: string): string {
	const pathObj = primaryCells.reduce((acc, cell: Cell, index: number) => {
		const header = headers[index];
		if (!header) return acc;
		const path = cell.value.val;
		const objectTitle = Object.values(header.cell.value.val ?? {})[0];
		if (path) {
			acc[objectTitle] = {
				'value': path,
				'index': index,
			};
		}
		return acc;
	}, {});

	return goalPathHash({ ...pathObj, date: date });
}

async function exportReport(report: Report, reprasentation: any, products: Product[], users: Owner[], evolutionStateRef: React.MutableRefObject<EvolutionState>, companies: Record<number, string>, campaigns: Record<number, Campaign>, includeNodes: boolean, isGoals: boolean, goalHashMap?: GoalHashMap, selectedPanel?: number) {
	const deltedProducts = await getDeletedProducts();
	const MAX_SHEET_NAME_LENGTH = 29;
	const valueToCell = function(val: Value | ComplexCell | Cell, columnIndex?: number): CellObject | undefined {
		const { value, tooltip }: { value: Value, tooltip?: Tooltip | null } = 'tag' in val ? { value: val, tooltip: undefined } : val;
		switch (value.tag) {
			case 0:
				return { t: 'n', f: `${value.val.percentage.numer} / ${value.val.percentage.denom}`, z: '0.00%' };
			case 1:
				return { t: 's', v: value.val.text };
			case 2:
				return { t: 's', v: translateToString(value.val.key) };
			case 3: {
				let res = translateToString(value.val.formattedKey.key);
				value.val.formattedKey.formats.forEach((replacementKey, i) => res = res?.replaceAll(`{{${i + 1}}}`, replacementKey));
				return { t: 's', v: res };
			}
			case 4:
				return { t: 'n', v: value.val.float, z: '0.00' };
			case 5:
				return { t: 'n', v: value.val.int };
			case 6:
				return { t: 's', v: value.val.colorDot };
			case 7: {
				if (tooltip == null || tooltip.content.tag != 1) return { t: 's', v: 'Piecharts are not supported here' };
                
				const str = tooltip.content.val.table.map(row => `${row[2].val?.['int']} x ${row[0].val?.['text']}`).join(', ');
				return { t: 's', v: str };
			}
			case 8:
				return { t: 's', v: reportingDateToString(value.val.date) };
			case 9:
				return { t: 'd', v: moment.unix(value.val.datetime).toDate() };
			case 10:
				return { t: 's', v: users.find(u => u.id === value.val.user)!.name };
			case 11: {
				const flat = evolutionStateRef.current[columnIndex ?? -1] === '+';
				const evo = calcEvolution(value.val.evolution);
				if (flat) return { t: 'n', v: evo?.flat, z: '0.00' };
				if (evo?.percentage !== undefined && isNaN(evo.percentage)) return { t: 'n', v: undefined, z: '0.00%' };
				return { t: 'n', v: evo?.percentage, z: '0.00%' };
			}
			case 12: {
				const product = products.find(p => p.uuid === value.val.product);
				if (!product) {
					const deletedProduct = deltedProducts.find(p => p.uuid === value.val.product);
					if (deletedProduct) {
						return { t: 's', v: deletedProduct.name + translateToString('reports.deleted_product') };
					}
					return { t: 's', v: 'Produit introuvable' };
				}
				return { t: 's', v: product.name };
			}
			case 13:
				return { t: 's', v: value.val.list.map(v => v.value).join(', ') };
			case 14:
				return { t: 's', v: companies[value.val.company] };
			case 15:
				return undefined;
			case 16:
				return undefined;
			case 17:
				return { t: 'n', f: `${value.val.average.numerator}/${value.val.average.denominator}`, z: '0.00' };
			case 18:
				return { t: 's', v: value.val.event.title };
			case 19:
				return { t: 's', v: users.find(u => u.id === value.val.contact)?.name ?? 'Contact introuvable' };
			case 20:
				return { t: 's', v: campaigns[value.val.campaign]?.name ?? '' };
			default:
				console.log(value);
				return { t: 's', v: Object.values(value.val)[0].toString() };
		}
	};

	const wb = utils.book_new();
	wb.Props = {
		Title: reprasentation.name,
		Subject: 'Exported report',
		Author: 'Sidely',
		CreatedDate: new Date()
	};
	let i = 0;

	for (const panel of report.panels) {
		if (isGoals && selectedPanel !== undefined && report.panels.indexOf(panel) !== selectedPanel) {
			continue;
		}
		const header: (CellObject | undefined)[] = [{ t: 's', v: 'Id du point de vente' }];
		header.push(...panel.headers.reduce((acc: (CellObject | undefined)[], header) => {
			const res = valueToCell(header.cell);
			if (!res) {
				const old = acc[acc.length - 1];
				if (old?.t === 's') {
					acc.push({
						t: 's',
						v: 'Variation - ' + old.v
					});
				} else {
					acc.push(undefined);
				}
			} else acc.push(res);
			if (isGoals && header.cell.value.tag === 8) {
				acc.push({ t: 's', v: '🎯' }, { t: 's', v: '%' });
			}
			return acc;
		}, []));
		const primaryCount = panel.headers.filter(h => h.primary).length;
		const nonPrimaryCount = panel.headers.filter(h => !h.primary).length;
		const rawName = valueToCell(panel.value)?.v?.toString() ?? (report.panels.length == 1 ? reprasentation.name : `Sheet ${i++}`);
		const str = safeStringTruncate(rawName, MAX_SHEET_NAME_LENGTH);
		wb.SheetNames.push(str);
		const rows = [header];
		let dateHeaders: string[] = [];
		if (isGoals) {
			dateHeaders = panel.headers.filter(h => !h.primary).reduce((acc: string[], header: Header) => {
				if (header.cell.value.tag !== 8) return acc;
				const dateStr = Object.values(header.cell.value.val.date.val)[0];
				acc.push(dateStr);
				return acc;
			}, []);
		}
		const exportRowRecursively = function(row: Row) {
			const primaryCells = row.primaryCell.map((cell, i) => valueToCell(cell, i));
			primaryCells.length = primaryCount;
			const dataCells = row.cells.reduce((acc: (CellObject | undefined)[], cell, i) => {
				const res = valueToCell(cell, i + primaryCount);
				acc.push(res);
				if (isGoals && panel.headers[i + primaryCount].cell.value.tag === 8) {
					const goalHash = getGoalPathHash(row.primaryCell, panel.headers, dateHeaders[i]);
					const goal = goalHashMap ? goalHashMap.get(goalHash) : undefined;
					if (!goal) {
						acc.push(undefined, undefined);
					}
					else {
						if (goal.evolution) {
							const goalEvolutionCell = valueToCell(goal.evolution);
							acc.push({ t: 'n', v: goal.value }, goalEvolutionCell);
						}
						else 
							acc.push({ t: 'n', v: goal.value }, undefined);
					}
				}
				return acc;
			}, []);
			dataCells.length = isGoals ? nonPrimaryCount * 3 : nonPrimaryCount;
			const clientCompanyId = row.primaryCell.find(c => c.value.val && 'company' in c.value.val)?.value.val!['company'] ?? undefined;
			if (row.rows.length === 0 || includeNodes)
				rows.push([{ t: 'n', v: clientCompanyId }, ...primaryCells, ...dataCells]);
			for (const subRow of row.rows) {
				for (let i = 0; i < row.primaryCell.length; i++) {
					if (!subRow.primaryCell[i] || subRow.primaryCell[i].value.tag === 15) {
						subRow.primaryCell[i] = row.primaryCell[i];
					}
				}
				exportRowRecursively(subRow);
			}
		};
		for (const row of panel.rows) {
			exportRowRecursively(row);
		}
		const worksheet = utils.aoa_to_sheet(rows);
		worksheet['!cols'] = header.map(h => ({ wch: Math.max(10, (h?.v?.toString().length ?? 7) + 3) }));
		wb.Sheets[str] = worksheet;
	}



	const out = writeFileXLSX(wb, `export-${reprasentation.name}-${(new Date()).toISOString()}.xlsx`, { bookType: 'xlsx', type: 'binary' });
	console.log(out);
}

export function PopupExportReport({
	isOpen,
	setIsReportExportOpen,
	report,
	products,
	owners,
	evolutionStateRef,
	companies,
	campaigns,
	stateData, 
	goalHashMap,
	isGoals,
	selectedPanel,
	warning,
	onServerExport,
} : {
	isOpen: boolean;
	setIsReportExportOpen: React.Dispatch<React.SetStateAction<boolean>>;
	report: Report;
	products: Product[];
	owners: Owner[];
	evolutionStateRef: React.MutableRefObject<EvolutionState>;
	companies: Record<number, string>;
	campaigns: Record<number, Campaign>;
	stateData: any;
	goalHashMap: GoalHashMap | undefined;
	isGoals: boolean;
	selectedPanel?: number;
	warning?: string;
	// when set (and outside the goals overlay, whose data lives client-side),
	// the export runs on the server: any size, delivered via the export toast
	onServerExport?: (includeNodes: boolean) => Promise<void>;
}) {
	const doExport = async(includeNodes: boolean) => {
		if (onServerExport && !isGoals) {
			await onServerExport(includeNodes);
		} else {
			await exportReport(report, stateData, products, owners, evolutionStateRef, companies, campaigns, includeNodes, isGoals, goalHashMap, selectedPanel);
		}
	};
	return (
		<div>
			<Popup
				isOpen={isOpen}
				popupMode={PopupMode.Centered}
				onClickOut={() => {
					setIsReportExportOpen(false);
				}}
				popupStyle={{ height: '200px', minHeight: '100px' }}
			>
				<Container>
					<Title>{translateToString('reports.export_report')}</Title>
					<Body>
						<Text>{translateToString('reports.ask_total_report')}</Text>
						<FlexDiv gap='6px'>
							<Button
								buttonStyle={ButtonStyle.White}
								onClick={() => {
									setIsReportExportOpen(false);
								}}>
								{translateToString('global.cancel')}
							</Button>
							<Button
								onClick={async() => {
									await doExport(true);
									setIsReportExportOpen(false);
								}}>
								{translateToString('reports.complete_report_export')}
							</Button>
							<Button
								onClick={async() => {
									await doExport(false);
									setIsReportExportOpen(false);
								}}>
								{translateToString('reports.export_report_without_totals')}
							</Button>
						</FlexDiv>
					</Body>
					<Text>{warning}</Text>
				</Container>
			</Popup>
		</div>
	);
}